import React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { notify } from 'src/utils/notifications';
import Paginator from './Paginator';
import TableV2 from './Tables/TableV2';
import TableStateWrapper, { TableStateProps } from './TableStateWrapper';

type PaginatedTableProps = {
	tableGrid: string;
	pageSize: number;
	currentPage: number;
	firstPageShown: number;
	totalItems: number;
	tableContentTop: JSX.Element;
	tableContentMiddle: JSX.Element;
	pageChangeCallback: (newPage: number, oldFirstPageShown: number) => void;
	hideHeadersOnMobile?: boolean;
	tableContentBottom?: JSX.Element;
	noBorder?: boolean;
	innerClassName?: string;
	fillSpace?: boolean;
	tableState: TableStateProps;
	notRounded?: boolean;
};

const PaginatedTableV2 = (props: PaginatedTableProps) => {
	const debouncedExecutePageChange = useDebouncedCallback(
		(newPage: number, oldFirstPageShown: number) => {
			props.pageChangeCallback(newPage, oldFirstPageShown);
		},
		500,
		{ leading: true }
	);

	const handlePageChange = (newPage: number, oldFirstPageShown: number) => {
		if (debouncedExecutePageChange.isPending()) {
			notify({
				id: 'paginationRateLimit',
				type: 'info',
				message: 'Too many requests',
				description:
					"You can jump directly to a history page by clicking the '...' button",
			});
		}
		debouncedExecutePageChange(newPage, oldFirstPageShown);
	};

	const paginator =
		props.totalItems > props.pageSize ? (
			<Paginator
				pageSize={props.pageSize}
				currentPage={props.currentPage}
				firstPageShown={props.firstPageShown}
				onPageChange={handlePageChange}
				maxCount={props.totalItems}
				leftChild={props.tableContentBottom ?? null}
			/>
		) : (
			props.tableContentBottom ?? null
		);

	return (
		<TableStateWrapper {...props.tableState} paginator={paginator}>
			<TableV2.Skeleton
				top={props.tableContentTop}
				middle={props.tableContentMiddle}
				hideHeadersOnMobile={props.hideHeadersOnMobile}
				noBorder={props.noBorder}
				innerClassName={props.innerClassName}
				fillSpace={props.fillSpace}
				notRounded={props.notRounded}
			/>
		</TableStateWrapper>
	);
};

export default PaginatedTableV2;
